import Head from 'next/head'
import Link from 'next/link'
import { getArticleDateTime } from '../../../utils/article-date-time'
import { PersonalTrainerCharts } from '@/index/service-category/personal-trainer/PersonalTrainerCharts'
import { ArticleByline } from '../../../components/commons/ArticleByline'
import { harveyFinn } from 'data/authors'

export async function getStaticProps() {
    const { modifiedDate, year } = getArticleDateTime()
    return {
        props: { modifiedDate, year },
        revalidate: 259200,
    }
}

export default function PersonalTrainerCostStatistics({ modifiedDate, year }) {
    const publishedDate = "2026-09-05T07:30:46+00:00"
    // Hand-maintained. Bump only when the figures change - deriving this from build time
    // advertised a fresh update on every ISR regeneration with no content change.
    const lastReviewed = "2026-09-20T00:00:00+08:00"
    const pageTitle = `Personal Trainer Cost Statistics Singapore [${year}]`
    const pageDescription = `Personal training cost statistics for Singapore: average spend by age group, trainer type preferences and training location breakdown, based on StringsSG platform data.`
    const canonical = "https://www.stringssg.com/sg/personal-trainer/personal-trainer-cost-statistics-singapore"

    return (
        <>
            <Head>
                <title>{pageTitle}</title>
                <meta charSet="utf-8" />
                <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
                <meta name="viewport" content="width=device-width, initial-scale=1.0" />
                <meta name="description" content={pageDescription} />
                <meta name="keywords" content="personal trainer cost statistics singapore, personal training spend singapore, fitness statistics singapore" />
                <meta name="author" content={harveyFinn.name} />
                <meta httpEquiv="content-language" content="en-sg" />
                <meta property="og:locale" content="en_SG" />
                <meta property="og:site_name" content="StringsSG" />
                <meta property="og:url" content={canonical} />
                <meta property="og:type" content="article" />
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={pageDescription} />
                <meta property="article:author" content={harveyFinn.name} />
                <meta property="article:published_time" content={publishedDate} />
                <meta property="article:modified_time" content={lastReviewed} />
                <meta property="og:image" content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/personal-trainer/personal-trainer.jpg" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:site" content="@stringssg" />
                <meta name="twitter:title" content={pageTitle} />
                <meta name="twitter:description" content={pageDescription} />
                <meta name="twitter:image" content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/personal-trainer/personal-trainer.jpg" />
                <link rel="icon" href="/favicon.ico" />
                <link rel="canonical" href={canonical} />
                <script type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify({
                            "@context": "https://schema.org",
                            "@graph": [
                                {
                                    "@type": "Article",
                                    "mainEntityOfPage": { "@type": "WebPage", "@id": canonical },
                                    "headline": pageTitle,
                                    "description": pageDescription,
                                    "image": ["https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/personal-trainer/personal-trainer.jpg"],
                                    "author": {
                                        "@type": "Person",
                                        "name": harveyFinn.name,
                                        "email": harveyFinn.email,
                                        "jobTitle": harveyFinn.jobTitle,
                                        "knowsAbout": harveyFinn.knowsAbout,
                                        "worksFor": { "@type": "Organization", "@id": "https://www.stringssg.com/#organization", "name": "StringsSG", "url": "https://www.stringssg.com" }
                                    },
                                    "publisher": {
                                        "@type": "Organization",
                                        "name": "StringsSG",
                                        "url": "https://www.stringssg.com",
                                        "logo": { "@type": "ImageObject", "url": "https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/logo.png" }
                                    },
                                    "about": { "@type": "Thing", "name": "Personal training cost statistics in Singapore" },
                                    "datePublished": publishedDate,
                                    "dateModified": lastReviewed
                                },
                                {
                                    "@type": "BreadcrumbList",
                                    "itemListElement": [
                                        { "@type": "ListItem", "position": 1, "name": "Personal Trainer", "item": "https://www.stringssg.com/sg/personal-trainer" },
                                        { "@type": "ListItem", "position": 2, "name": "Cost Statistics" }
                                    ]
                                }
                            ]
                        })
                    }}
                />
            </Head>

            <div className="bg-slate-900 min-h-screen">
                <div className="max-w-5xl mx-auto px-4 py-10 text-white/70">
                    <div className="flex items-center gap-2 text-white/40 text-sm mb-4">
                        <Link href="/sg/personal-trainer" className="hover:text-white/60 transition-colors">Personal Trainer</Link>
                        <span>/</span>
                        <span className="text-white/60">Cost Statistics</span>
                    </div>

                    <h1 className="text-2xl md:text-4xl text-white font-extrabold mb-4">
                        Personal Trainer Cost Statistics in Singapore
                    </h1>

                    <ArticleByline
                        author={harveyFinn}
                        publishedDate={publishedDate}
                        modifiedDate={lastReviewed}
                        variant="header"
                    />

                    <p className="leading-relaxed mb-4">
                        This page collects what StringsSG sees on its own platform about how much Singaporeans spend on personal training, which trainer types
                        they choose, and where they prefer to train. For the full pricing guide, including rates by experience, location and package size, see{' '}
                        <Link href="/sg/personal-trainer/personal-trainer-rates-singapore" prefetch={false} className="text-orange-400 hover:text-orange-300 underline">
                            personal trainer rates in Singapore
                        </Link>.
                    </p>

                    {/* TODO(StringsSG): replace the bracketed placeholders below with the confirmed
                        figures from the transaction dataset before promoting this page.
                        Do not publish invented numbers here. */}
                    <div className="bg-white/3 border-l-4 border-white/20 rounded-r-lg p-4 my-6">
                        <h2 className="text-sm font-semibold text-white/70 mb-2">Methodology</h2>
                        <ul className="text-sm text-white/50 leading-relaxed space-y-1">
                            <li><strong className="text-white/70">Source:</strong> StringsSG personal training bookings and enquiries.</li>
                            <li><strong className="text-white/70">Prices shown:</strong> per session, before promotional discounts.</li>
                            <li><strong className="text-white/70">Packages:</strong> converted to an effective per-session value</li>
                        </ul>
                        <p className="text-xs text-white/40 mt-3 leading-relaxed">
                            These figures describe activity on the StringsSG platform. They are not a nationally representative survey of Singapore, and no official
                            body in Singapore publishes personal training rates. Treat them as directional guidance rather than a fixed market tariff.
                        </p>
                    </div>
                </div>

                <PersonalTrainerCharts />

                <div className="max-w-5xl mx-auto px-4 pb-12 text-white/70">
                    <ArticleByline author={harveyFinn} variant="footer" />

                    <div className="text-center mt-8">
                        <Link href="/sg/personal-trainer/personal-trainer-rates-singapore" prefetch={false}
                            className="inline-flex items-center gap-2 px-8 py-4 bg-linear-to-r from-orange-500 to-amber-500 text-white font-bold rounded-full shadow-lg shadow-orange-500/20 hover:opacity-90 transition-all duration-300">
                            See Personal Trainer Rates in Singapore
                        </Link>
                    </div>
                </div>
            </div>
        </>
    )
}
